import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight, Plus } from "lucide-react";
import Seo from "@/components/Seo";
import { EASE, Eyebrow, FadeUp, scrollToId } from "@/lib/motion";
import { FAQ_CATEGORIES } from "@/data/faqs";

const answerText = (a) =>
  a.map((b) => (b.type === "p" ? b.text : b.items.join(" "))).join(" ");

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQ_CATEGORIES.flatMap((c) =>
    c.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: answerText(f.a) },
    }))
  ),
};

const CATEGORY_LINKS = {
  seo: { to: "/seo", label: "Explore our SEO services" },
  "geo-aio": {
    to: "/ai-visibility",
    label: "See the SEO + AI Visibility program — $397/mo founding rate",
  },
  "paid-ads": {
    to: "/ad-remarketing",
    label: "Explore our Google Ads Management",
  },
  "website-optimization": {
    to: "/website-optimization",
    label: "Explore our website optimization services",
  },
};

function AnswerBlocks({ a }) {
  return a.map((b, bi) =>
    b.type === "p" ? (
      <p key={bi} className="mt-3 text-lg leading-relaxed text-mist first:mt-0">
        {b.text}
      </p>
    ) : (
      <ul key={bi} className="mt-3 space-y-2 first:mt-0">
        {b.items.map((item) => (
          <li
            key={item}
            className="flex items-start gap-3 text-lg leading-relaxed text-mist"
          >
            <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-flame" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    )
  );
}

function FaqItem({ catId, qIdx, faq, open, onToggle }) {
  return (
    <div
      data-testid={`faq-item-${catId}-${qIdx}`}
      className="glass rounded-2xl border border-slate-200"
    >
      <button
        data-testid={`faq-question-${catId}-${qIdx}`}
        aria-expanded={open}
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-6 px-6 py-5 text-left md:px-7"
      >
        <h3 className="font-display text-lg font-bold tracking-tight text-slate-900 md:text-xl">
          {faq.q}
        </h3>
        <Plus
          className={`h-5 w-5 shrink-0 text-flame transition-transform duration-300 ${
            open ? "rotate-45" : ""
          }`}
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            data-testid={`faq-answer-${catId}-${qIdx}`}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-6 md:px-7">
              <AnswerBlocks a={faq.a} />
              {CATEGORY_LINKS[catId] && (
                <Link
                  to={CATEGORY_LINKS[catId].to}
                  data-testid={`faq-link-${catId}-${qIdx}`}
                  className="mt-5 inline-flex items-center gap-1.5 text-base font-semibold text-flame transition-all hover:gap-2.5"
                >
                  {CATEGORY_LINKS[catId].label}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function FaqPage() {
  const [open, setOpen] = useState({ cat: "website-marketing", idx: 0 });
  const toggle = (cat, idx) => () =>
    setOpen((o) =>
      o.cat === cat && o.idx === idx ? { cat: null, idx: null } : { cat, idx }
    );

  return (
    <main data-testid="faq-page">
      <Seo
        title="Website Marketing & SEO FAQ — ZD Design"
        description="Plain-language answers about website marketing budgets, SEO, social media, Google Ads vs. Facebook ads and website optimization — from ZD Design, Kansas City."
        path="/faq"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <section className="grain relative overflow-hidden pb-12 pt-40 md:pb-14 md:pt-48">
        <div className="pointer-events-none absolute -right-40 top-0 h-[420px] w-[420px] rounded-full bg-flame/10 blur-[140px]" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
          <FadeUp>
            <Link
              to="/"
              data-testid="faq-back-link"
              className="inline-flex items-center gap-2 font-mono text-base uppercase tracking-[0.2em] text-faint transition-colors hover:text-flame"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back home
            </Link>
            <Eyebrow>FAQ</Eyebrow>
            <h1 className="mt-5 max-w-3xl font-display text-4xl font-extrabold leading-[0.98] tracking-tight text-slate-900 sm:text-5xl md:text-6xl">
              Frequently asked questions<span className="text-flame">.</span>
            </h1>
            <p className="mt-7 max-w-2xl text-lg leading-relaxed text-mist md:text-xl">
              Straight answers on website marketing, SEO, social media, paid
              ads, website optimization, GEO and AIO — the questions we hear
              most from small business owners.
            </p>
          </FadeUp>
          <FadeUp delay={0.1}>
            <div className="mt-9 flex flex-wrap gap-3">
              {FAQ_CATEGORIES.map((c) => (
                <button
                  key={c.id}
                  data-testid={`faq-chip-${c.id}`}
                  onClick={() => scrollToId(`faq-${c.id}`)}
                  className="rounded-2xl border border-slate-200 bg-white/60 px-5 py-2.5 text-sm font-semibold text-slate-700 transition-all duration-300 hover:border-flame/40 hover:text-flame"
                >
                  {c.title}
                </button>
              ))}
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="border-t border-slate-200 py-16 md:py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-8">
          {FAQ_CATEGORIES.map((c) => (
            <div
              key={c.id}
              id={`faq-${c.id}`}
              data-testid={`faq-section-${c.id}`}
              className="scroll-mt-28 pb-14 last:pb-0"
            >
              <h2 className="font-display text-2xl font-extrabold tracking-tight text-slate-900 md:text-3xl">
                {c.title}
              </h2>
              <div className="mt-6 space-y-4">
                {c.faqs.map((f, i) => (
                  <FaqItem
                    key={f.q}
                    catId={c.id}
                    qIdx={i}
                    faq={f}
                    open={open.cat === c.id && open.idx === i}
                    onToggle={toggle(c.id, i)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="border-t border-slate-200 py-16 md:py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-8">
          <FadeUp>
            <div
              data-testid="faq-cta-card"
              className="glass relative overflow-hidden rounded-3xl border border-slate-200 p-8 text-center md:p-12"
            >
              <div className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full bg-flame/15 blur-[90px]" />
              <h2 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 md:text-4xl">
                Still have questions<span className="text-flame">?</span>
              </h2>
              <p className="mx-auto mt-4 max-w-xl text-lg leading-relaxed text-mist">
                Book a free 30-minute strategy call with Todd — plain-language
                answers about your website, SEO and advertising, no strings
                attached.
              </p>
              <Link
                to="/free-strategy-call"
                data-testid="faq-cta-button"
                className="mt-8 inline-flex items-center gap-2 rounded-2xl bg-flame px-7 py-3.5 font-mono text-base font-semibold uppercase tracking-[0.15em] text-obsidian transition-all duration-300 hover:shadow-[0_0_28px_rgba(255,85,0,0.45)]"
              >
                Schedule a free strategy call
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
